import { POST_AUTH_DEFAULT, safeNext, withNext } from "@/lib/auth/safe-next";

/** Every onboarding URL, built in ONE place, so a pending deep link (class
 *  invite `/join`, `/pricing`) survives every hop between /onboarding,
 *  sign-in, the submit and /onboarding/finish. Callers never concatenate an
 *  onboarding URL themselves. */

export const ONBOARDING_PATH = "/onboarding";
export const ONBOARDING_FINISH_PATH = "/onboarding/finish";

/** Is this pathname part of the onboarding? The one predicate: the proxy's
 *  onboarding-gate exemption delegates here too. */
export function isOnboardingPath(pathname: string): boolean {
  return pathname === ONBOARDING_PATH || pathname.startsWith(`${ONBOARDING_PATH}/`);
}

/** How many `?next=` wrappers deep a nested deep link is followed. */
export const MAX_NEXT_DEPTH = 5;

/** The deep link to land on after onboarding, or "" for none.
 *
 *  Same-origin (`safeNext`), and never a path inside the onboarding: the
 *  flow's own links back into itself (hook / account "Sign in" →
 *  /login?next=/onboarding…, then /login's "Create an account") wrap the
 *  real destination in `/onboarding?next=…`. Unwrap to the inner link rather
 *  than discard it, so the invite survives the detour; give up after a few
 *  levels of nesting rather than loop. */
export function onboardingDeepLink(raw: string | null | undefined): string {
  let next = safeNext(raw, "");
  for (let depth = 0; depth < MAX_NEXT_DEPTH && next; depth++) {
    const url = new URL(next, "http://onboarding.local");
    // Home is where a finished onboarding lands anyway; with a query string
    // (`/?utm_source=…`) it must not count as a deep link, or finish would
    // "forward" there and skip the paywall + first scenario.
    if (url.pathname === POST_AUTH_DEFAULT) return "";
    if (!isOnboardingPath(url.pathname)) return next;
    next = safeNext(url.searchParams.get("next"), "");
  }
  return "";
}

// Every builder normalizes its argument through onboardingDeepLink, so the
// module never EMITS a nested `/onboarding?next=/onboarding…`; the unwrap loop
// above only has to defend against external input.

/** The wizard, carrying a pending deep link. */
export function onboardingUrl(deepLink?: string | null): string {
  return withNext(ONBOARDING_PATH, onboardingDeepLink(deepLink) || null);
}

/** The post-submit steps (paywall / first scenario / instructor handoff),
 *  carrying a pending deep link that finish forwards to instead. */
export function finishUrl(deepLink?: string | null): string {
  return withNext(ONBOARDING_FINISH_PATH, onboardingDeepLink(deepLink) || null);
}

/** One post-submit step. No deep link: a pending one is forwarded before any
 *  step renders. */
export function finishStepUrl(step: string): string {
  return `${ONBOARDING_FINISH_PATH}?${new URLSearchParams({ step })}`;
}

/** For a visitor who turns out to be ALREADY onboarded: finish clears this
 *  device's leftover draft, then forwards to the deep link or home, skipping
 *  the post-submit steps. An explicit flag rather than a `next=/` sentinel,
 *  because withNext() deliberately never emits the default landing page. */
export const FINISH_FORWARD_PARAM = "forward";
export function finishForwardUrl(deepLink?: string | null): string {
  const params = new URLSearchParams({ [FINISH_FORWARD_PARAM]: "1" });
  const next = onboardingDeepLink(deepLink);
  if (next) params.set("next", next);
  return `${ONBOARDING_FINISH_PATH}?${params}`;
}
